import { useEffect, useRef, useState } from "react";
import { Box, Center, Image, Skeleton, useColorModeValue } from "@chakra-ui/react";
import { BBox } from "geojson";
import { CollecticonPicture } from "@devseed-ui/collecticons-chakra";

import API from "../api";

type Props = {
  sceneId: string;
  bbox?: BBox;
  width: number;
  height: number;
  brightness?: number;
  contrast?: number;
}

function SceneThumbnail({ sceneId, bbox, width, height, brightness, contrast }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const scale = window.devicePixelRatio;
  const [ image, setImage ] = useState<string>();
  const [ isLoading, setIsLoading ] = useState<boolean>(false);

  const cardThumbBgColor = useColorModeValue("gray.100", "base.600");
  const iconColor = useColorModeValue("primary.700", "gray.100");

  useEffect(() => {
    // Lazy-load the thumbnail so we don't overwhelm the tiler
    if (!ref.current) return;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some(({ isIntersecting }) => isIntersecting)) return;
      setIsLoading(true);
      API.getScenePreview(sceneId, bbox?.join(), Math.floor(width * scale), Math.floor(height * scale), brightness, contrast)
        .then(r => {
          if (r.status < 400) {
            r.arrayBuffer().then(b => {
              const url = window.URL.createObjectURL(new Blob([b]));
              setImage(url);
            });
          }
        })
        .catch(() => setImage(undefined))
        .finally(() => setIsLoading(false));
    });

    observer.observe(ref.current);
    return () => {
      observer.disconnect();
    };
  }, [bbox, height, scale, sceneId, width, brightness, contrast]);

  // const w = `${width}px`;
  const h = `${height}px`;

  return (
    <Box height={h} bg={cardThumbBgColor} position="relative" ref={ref} overflow="hidden">
      <Skeleton isLoaded={!isLoading} height="100%">
        {image ? (
          <Image
            width="100%"
            height="100%"
            src={image}
            objectFit="cover"
          />
        ) : (
          <Center h="100%" position="relative" opacity="0.5">
            { bbox ? (
              <CollecticonPicture size="8" color={iconColor} meaningful title="Scene image is still processing, or is unavailable" />
            ) : (
              <>
                <CollecticonPicture size="8" color="gray.500" meaningful title="This scene does not have any images" />
                <Box
                  bgColor="gray.500"
                  height="4px"
                  width="48px"
                  position="absolute"
                  transform="rotate(-42deg)"
                />
              </>
            )}
          </Center>
        )}
      </Skeleton>
    </Box>
  );
}

export default SceneThumbnail;
